import React, {useCallback, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './SearchFilterResults.scss';
import {
  isDuplicateTranscriptNameError,
  TranscriptItemDataType,
} from '../../AiInvestigateUtils';
import TranscriptListItem from '../../transcipts/transcript-list-item/TranscriptListItem';
import {
  useDeleteInvestigateTranscript,
  useEditInvestigateTranscript,
} from '../../../../../hooks/ai/investigate/useInvestigate';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../shared/constants';
import {useTranscriptPanelSort} from '../TranscriptPanelContext';

interface SearchFilterResultsProps {
  transcripts: TranscriptItemDataType[];
  setTranscripts: React.Dispatch<
    React.SetStateAction<TranscriptItemDataType[]>
  >;
  selectedTranscriptId?: number;
  onItemClick?: (item: TranscriptItemDataType) => void;
  hasMore: boolean;
  isFetching: boolean;
  onLoadMore: () => void;
  onTranscriptDeleted?: (id: number) => void;
}
const ADJUSTMENT_HEIGHT = 100;
const SearchFilterResults: React.FC<SearchFilterResultsProps> = ({
  transcripts,
  setTranscripts,
  selectedTranscriptId,
  onItemClick,
  hasMore,
  isFetching,
  onLoadMore,
  onTranscriptDeleted,
}) => {
  const [focusedId, setFocusedId] = useState(-1);
  const focusViaKeyboardRef = useRef(false);
  const orderedIds = transcripts.map((item) => item.transcriptId);
  const selectedSortKey = useTranscriptPanelSort();

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const {mutateAsync: editTranscript} = useEditInvestigateTranscript();
  const handleRenameTranscript = useCallback(
    async (id: number, newName: string, newDescription: string) => {
      try {
        const {transcriptId, transcriptName, description, updatedDate} =
          await editTranscript({
            id,
            payload: {
              transcriptName: newName,
              description: newDescription,
            },
          });

        setTranscripts((prev) =>
          prev.map((item) =>
            item.transcriptId === transcriptId
              ? {
                  ...item,
                  transcriptName,
                  description,
                  updatedDate,
                }
              : item
          )
        );
        return true;
      } catch (error) {
        setToastMessage(
          isDuplicateTranscriptNameError(error)
            ? t('ai.investigate.listItem.editModalContent.duplicateChatName')
            : t('ai.investigate.listItem.editModalContent.errorMessage')
        );
        setShowToast(true);
        return false;
      }
    },
    [editTranscript, setTranscripts]
  );

  const {mutate: deleteTranscript} = useDeleteInvestigateTranscript();

  const handleDeleteTranscript = useCallback(
    (id: number) => {
      deleteTranscript(id, {
        onSuccess: () => {
          setTranscripts((prev) =>
            prev.filter((item) => item.transcriptId !== id)
          );
          onTranscriptDeleted?.(id);
        },
        onError: (err) => {
          console.error('Delete failed', err);
        },
      });
    },
    [deleteTranscript, onTranscriptDeleted]
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
    const isBottom =
      scrollTop + clientHeight >= scrollHeight - ADJUSTMENT_HEIGHT;

    if (isBottom && !isFetching && hasMore) {
      onLoadMore();
    }
  };

  if (!transcripts.length) {
    return (
      <Box className="filtered-transcript-list">
        <Typography
          className="transcript-panel-no-transcripts typo-text body3_size"
          data-testid="search-filter-results-no-results-found"
        >
          {t('ai.investigate.transcriptPanel.noResultsFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="filtered-transcript-list" onScroll={handleScroll}>
      {transcripts.map((item, index) => (
        <TranscriptListItem
          key={item.transcriptId}
          item={item}
          index={index}
          focusedId={focusedId}
          setFocusedId={setFocusedId}
          orderedIds={orderedIds}
          focusViaKeyboardRef={focusViaKeyboardRef}
          selected={selectedTranscriptId === item.transcriptId}
          showAvatar={item.isMyTranscript === false}
          onClick={onItemClick}
          onDelete={item.isMyTranscript ? handleDeleteTranscript : undefined}
          onRename={item.isMyTranscript ? handleRenameTranscript : undefined}
        />
      ))}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={[ToastType.Error]}
          onClose={() => setShowToast(false)}
          title="Error"
          data-testid="edit-transcript-toast"
          setErrorIconAsAlert
        />
      )}
    </Box>
  );
};

export default SearchFilterResults;
